import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoEquipmentCalibrations from "config/demo/equipmentCalibration.json";
import EquipmentCalibrationForm from "components/agro/equipmentCalibration/EquipmentCalibrationForm";

const EquipmentCalibration = () => {
  const [items] = useState(
    demoEquipmentCalibrations && demoEquipmentCalibrations.slice
      ? demoEquipmentCalibrations.slice(0, 10)
      : []
  );
  const [itemDetails, setItemDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        actions: (
          <>
            <ActionIcon
              name={
                itemDetails?.id === it.id
                  ? strings.calibration.hide
                  : strings.calibration.show
              }
              icon={itemDetails?.id === it.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                itemDetails?.id === it.id
                  ? setItemDetails(null)
                  : setItemDetails(it);
              }}
            />
            <ActionIcon
              name={strings.calibration.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", it.id)}
            />
            <ActionIcon
              name={strings.calibration.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: it.id,
                  itemName: it.equipmentName || it.id,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, itemDetails?.id]
  );

  const openModal = (action, id = null) =>
    setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    {
      key: "date",
      label: strings.table.headers.date,
      _style: { width: "18%" },
    },
    {
      key: "equipmentId",
      label: strings.table.headers.equipment,
      _style: { width: "28%" },
    },
    {
      key: "serialNumber",
      label: strings.table.headers.serialNumber,
      _style: { width: "18%" },
    },
    {
      key: "performedById",
      label: strings.table.headers.performedBy,
      _style: { width: "18%" },
    },
    { key: "actions", label: "", _style: { width: "18%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.calibration.add}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
          />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!itemDetails?.id} style={styles.collapseCard}>
          <CCard key={itemDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "
                {itemDetails?.equipmentName || itemDetails?.id}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.date}:</b>{" "}
                {itemDetails?.date
                  ? moment(itemDetails.date).format("YYYY-MM-DD")
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.equipment}:</b>{" "}
                {itemDetails?.equipmentName ?? itemDetails?.equipmentId ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.serialNumber}:</b>{" "}
                {itemDetails?.serialNumber ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.theoreticalNozzleFlow}:</b>{" "}
                {itemDetails?.theoreticalNozzleFlowCcPerMin ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.actualNozzleFlow}:</b>{" "}
                {itemDetails?.actualNozzleFlowCcPerMin ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.registers}:</b>{" "}
                {itemDetails?.registers
                  ? JSON.stringify(itemDetails.registers)
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.nozzleCondition}:</b>{" "}
                {itemDetails?.nozzleCondition ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.nozzleChange}:</b>{" "}
                {typeof itemDetails?.nozzleChange === "boolean"
                  ? itemDetails.nozzleChange
                    ? "Sí"
                    : "No"
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.performedBy}:</b>{" "}
                {itemDetails?.performedByName ??
                  itemDetails?.performedById ??
                  "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.observations}:</b>{" "}
                {itemDetails?.observations ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.photoEvidence}:</b>{" "}
                {(Array.isArray(itemDetails?.photoEvidence)
                  ? itemDetails.photoEvidence.join(", ")
                  : itemDetails?.photoEvidence) ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {itemDetails?.createdAt
                  ? moment(itemDetails.createdAt).format("YYYY-MM-DD HH:mm:ss")
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.itemName,
        })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalForm.visible}
          onClose={() => setModalForm({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalForm.action === "create"
                ? strings.modal.addTitle
                : strings.modal.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <EquipmentCalibrationForm
                type={modalForm.action}
                initialValues={
                  modalForm.action === "update"
                    ? items.find((i) => i.id === modalForm.itemId) || {}
                    : {}
                }
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Calibración de Equipos",
    calibration: {
      add: "Registrar Calibración",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        date: "Fecha",
        equipment: "Equipo",
        serialNumber: "Serial",
        performedBy: "Persona",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Calibración",
      date: "Fecha",
      equipment: "Equipo",
      serialNumber: "Serial",
      theoreticalNozzleFlow: "Descarga teórica (cc/min)",
      actualNozzleFlow: "Descarga real (cc/min)",
      registers: "Registros (T1,T2,T3,avg)",
      nozzleCondition: "Estado boquilla",
      nozzleChange: "Cambio boquilla",
      performedBy: "Persona que realiza",
      observations: "Observaciones",
      photoEvidence: "Evidencia fotográfica",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la calibración {name}?",
      addTitle: "Registrar Calibración",
      updateTitle: "Actualizar Calibración",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default EquipmentCalibration;
